import { api } from "./api";

export const DEFAULT_CONNECTION = {
  transport: "download", // download | webserial | bridge
  baud: 9600,
  flow: "none",
  shopCode: "",
  targetType: "tcp", // tcp | serial | printer
  host: "192.168.1.50",
  port: 9100,
  serialPort: "",
  printerName: "",
};

const enc = (text) => {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xff;
  return bytes;
};

export const webSerialSupported = () => typeof navigator !== "undefined" && "serial" in navigator;

export async function sendWebSerial(commands, { baud, flow }, onProgress) {
  if (!webSerialSupported()) throw new Error("Web Serial is not supported in this browser. Use Chrome or Edge on desktop.");
  const ports = await navigator.serial.getPorts().catch(() => []);
  const port = ports.length === 1 ? ports[0] : await navigator.serial.requestPort();
  await port.open({ baudRate: Number(baud) || 9600, flowControl: flow === "rtscts" ? "hardware" : "none" });
  const writer = port.writable.getWriter();
  const data = enc(commands);
  try {
    for (let i = 0; i < data.length; i += 1024) {
      await writer.write(data.slice(i, i + 1024));
      onProgress?.(Math.min(1, (i + 1024) / data.length));
    }
  } finally {
    writer.releaseLock();
    await port.close();
  }
  return data.length;
}

export const webUsbSupported = () => typeof navigator !== "undefined" && "usb" in navigator;

export async function sendWebUsb(commands, onProgress) {
  if (!webUsbSupported()) throw new Error("WebUSB is not supported in this browser. Use Chrome or Edge on desktop.");
  // Chrome remembers granted devices: reuse the plotter from last time, only prompt when none is authorised/plugged in.
  const granted = (await navigator.usb.getDevices().catch(() => [])).find((d) => d.configurations?.length);
  const device = granted || await navigator.usb.requestDevice({ filters: [{ classCode: 7 }, {}] });
  await device.open();
  try {
    if (device.configuration === null) await device.selectConfiguration(1);
    let iface = null, ep = null;
    for (const i of device.configuration.interfaces) {
      for (const alt of i.alternates) {
        const out = alt.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
        if (out && (alt.interfaceClass === 7 || !ep)) { iface = i; ep = out; }
      }
    }
    if (!ep) throw new Error("No bulk output endpoint found on this USB device.");
    try { await device.claimInterface(iface.interfaceNumber); }
    catch (e) { throw new Error(`Windows is holding the cutter's USB port (${e.message}). Use the Xpattern Bridge (USB printer mode) or a serial cable instead.`); }
    const data = enc(commands);
    for (let i = 0; i < data.length; i += 4096) {
      await device.transferOut(ep.endpointNumber, data.slice(i, i + 4096));
      onProgress?.(Math.min(1, (i + 4096) / data.length));
    }
    await device.releaseInterface(iface.interfaceNumber);
    return data.length;
  } finally {
    await device.close().catch(() => {});
  }
}

// `m` is a machine profile (or a bare command_format string). Roland/GCC profiles (panel_conditions) use the plotter's own speed/force.
const dialect = (m) => {
  const format = typeof m === "string" ? m : m?.command_format || "hpgl";
  const panel = typeof m === "object" && !!m?.panel_conditions;
  return { format, hp: panel ? "" : "VS20;\nFS120;\n", gp: panel ? "" : "!20\x03FX14,1\x03", dm: panel ? "" : "V20\n" };
};

export const BAUD_RATES = [2400, 4800, 9600, 19200, 38400, 57600, 115200];

export const unitsPerInch = (format) => ({ gpgl: 254, dmpl: 1000 }[format] || 1016);

// DMPL closed rectangle: pen-up to the first corner, pen-down through the rest, back to origin, end of job.
const dmplRect = (dm, pts) => `;:\nH\nA\nL0\nEC1\n${dm}U ${pts[0]}\n${pts.slice(1).map((q) => `D ${q}`).join("\n")}\nD ${pts[0]}\nU 0,0\n@`;

export function testSquareCommands(m, sizeIn = 2) {
  const { format, hp, gp, dm } = dialect(m);
  if (format === "gpgl") { const u = Math.round(sizeIn * 254); return `:\x03H\x03${gp}M0,0\x03D${u},0,${u},${u},0,${u},0,0\x03M0,0\x03H\x03`; }
  if (format === "dmpl") { const u = Math.round(sizeIn * 1000); return dmplRect(dm, ["0,0", `${u},0`, `${u},${u}`, `0,${u}`]); }
  const u = Math.round(sizeIn * 1016);
  return `IN;\nPA;\n${hp}SP1;\nPU;\nPA0,0;\nPD;\nPA${u},0,${u},${u},0,${u},0,0;\nPU;\nPA0,0;`;
}

// Feed check (no cut): pen-up run to the far end of the job length and back at the cutting speed, so the operator can watch the film
// edge against the pinch roller / ruler before any blade drops. The edge walking across the length = the film is not loaded square.
export function feedCheckCommands(m, lengthIn = 60) {
  const { format, hp, gp, dm } = dialect(m);
  const L = Math.round(lengthIn * unitsPerInch(format));
  if (format === "gpgl") return `:\x03H\x03${gp}M${L},0\x03M0,0\x03H\x03`;
  if (format === "dmpl") return `;:\nH\nA\nL0\nEC1\n${dm}U ${L},0\nU 0,0\n@`;
  return `IN;\nPA;\n${hp}SP1;\nPU;\nPA${L},0;\nPA0,0;`;
}

// Long-feed diagnostic: two 34" rails along the feed, 2" apart, with rungs at 0 / 12 / 24 / 33" — each rail and rung is its own
// pen-up/pen-down run. Rungs that miss the rails = film tracking drift over long feeds; anything past 32.25" landing wrong = the
// plotter's firmware only takes 16-bit coordinates (> 32767 units).
export function ladderTestCommands(m, lengthIn = 34, widthIn = 2) {
  const { format, hp, gp, dm } = dialect(m);
  const upi = unitsPerInch(format);
  const L = Math.round(lengthIn * upi), W = Math.round(widthIn * upi);
  const runs = [[[L, 0], [0, 0]], [[L, W], [0, W]], ...[0, 12, 24, lengthIn - 1].map((f) => { const x = Math.round(f * upi); return [[x, 0], [x, W]]; })];  // rails cut inward like a real job
  if (format === "gpgl") return `:\x03H\x03${gp}${runs.map(([a, b]) => `M${a[0]},${a[1]}\x03D${b[0]},${b[1]}\x03`).join("")}M0,0\x03H\x03`;
  if (format === "dmpl") return `;:\nH\nA\nL0\nEC1\n${dm}${runs.map(([a, b]) => `U ${a[0]},${a[1]}\nD ${b[0]},${b[1]}\n`).join("")}U 0,0\n@`;
  return `IN;\nPA;\n${hp}SP1;\nPU;\n${runs.map(([a, b]) => `PA${a[0]},${a[1]};\nPD;\nPA${b[0]},${b[1]};\nPU;\n`).join("")}PA0,0;`;
}

// Rectangle for calibration cuts: w across the roll, h along the feed, with the current calibration scale applied.
export function calibrationCommands(m, wIn, hIn, cal = {}) {
  const { format, hp, gp, dm } = dialect(m);
  const sw = cal.scale_width || 1, sl = cal.scale_length || 1;
  if (format === "gpgl") { const a = Math.round(hIn * 254 * sl), b = Math.round(wIn * 254 * sw); return `:\x03H\x03${gp}M0,0\x03D${a},0,${a},${b},0,${b},0,0\x03M0,0\x03H\x03`; }
  if (format === "dmpl") { const a = Math.round(hIn * 1000 * sl), b = Math.round(wIn * 1000 * sw); return dmplRect(dm, ["0,0", `${a},0`, `${a},${b}`, `0,${b}`]); }
  const a = Math.round(hIn * 1016 * sl), b = Math.round(wIn * 1016 * sw);
  return `IN;\nPA;\n${hp}SP1;\nPU;\nPA0,0;\nPD;\nPA${a},0,${a},${b},0,${b},0,0;\nPU;\nPA0,0;`;
}

// 1" blade-depth test square placed in the middle of the chosen film width.
export function bladeTestCommands(m, filmWidthIn, cal = {}) {
  const { format, hp, gp, dm } = dialect(m);
  const sw = cal.scale_width || 1, sl = cal.scale_length || 1;
  const upi = unitsPerInch(format);
  const s = Math.round(1 * upi * sl), y0 = Math.round(Math.max(0, filmWidthIn / 2 - 0.5) * upi * sw), y1 = y0 + Math.round(1 * upi * sw);
  if (format === "gpgl") return `:\x03H\x03${gp}M0,${y0}\x03D${s},${y0},${s},${y1},0,${y1},0,${y0}\x03M0,0\x03H\x03`;
  if (format === "dmpl") return dmplRect(dm, [`0,${y0}`, `${s},${y0}`, `${s},${y1}`, `0,${y1}`]);
  return `IN;\nPA;\n${hp}SP1;\nPU;\nPA0,${y0};\nPD;\nPA${s},${y0},${s},${y1},0,${y1},0,${y0};\nPU;\nPA0,0;`;
}

// Pen-up move for the direction test: x along the feed, y across (inches, calibration scale applied). Negative values allowed.
export function moveCommands(m, xIn, yIn, cal = {}, absolute = false) {
  const { format } = dialect(m);
  const sw = cal.scale_width || 1, sl = cal.scale_length || 1;
  const upi = unitsPerInch(format);
  const x = Math.round(xIn * upi * sl), y = Math.round(yIn * upi * sw);
  if (format === "gpgl") return `:\x03H\x03M${x},${y}\x03`;
  if (format === "dmpl") return absolute ? `;:\nH\nA\nEC1\nU ${x},${y}\n@` : `;:\nR\nEC1\nU ${x},${y}\n@`;
  return absolute ? `IN;\nPA;\nSP1;\nPU;\nPA${x},${y};` : `IN;\nPR;\nSP1;\nPU;\nPR${x},${y};`;
}

export async function sendViaTransport(commands, c, onProgress) {
  if (c.transport === "webserial") return `${await sendWebSerial(commands, c, onProgress)} bytes at ${c.baud} baud`;
  if (c.transport === "webusb") return `${await sendWebUsb(commands, onProgress)} bytes over USB`;
  if (c.transport === "bridge") {
    const r = await relayRun(c.shopCode, "cut", bridgeTarget(c), commands, onProgress);
    const dest = c.targetType === "tcp" ? `${c.host}:${c.port}` : c.targetType === "printer" ? (r.target?.printerName || c.printerName) : c.serialPort;
    return `${r.bytes} bytes to ${dest}${r.rerouted_from ? ` (not "${r.rerouted_from}" — that Windows driver queue makes the plotter feed the roll without cutting)` : ""}`;
  }
  return "";
}

const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const newShopCode = () => Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join("");

export const loadConnection = (shopCode, uid) => {
  // A different account signed in on this browser: never inherit its pairing code / plotter target.
  if (uid && localStorage.getItem("xpattern.connection.uid") !== uid) { localStorage.removeItem("xpattern.connection"); localStorage.setItem("xpattern.connection.uid", uid); }
  const c = { ...DEFAULT_CONNECTION, ...(JSON.parse(localStorage.getItem("xpattern.connection") || "null") || {}) };
  if (shopCode && c.shopCode !== shopCode) { c.shopCode = shopCode; c.pinned = false; c.transport = c.transport === "bridge" ? "download" : c.transport; }
  if (!c.shopCode) c.shopCode = newShopCode();
  localStorage.setItem("xpattern.connection", JSON.stringify(c));
  return c;
};

const apiError = (e, fallback) => new Error(e?.response?.data?.detail || e?.message || fallback);

export async function bridgeStatus(code) {
  try { return await api.relayStatus(code); } catch (e) { throw apiError(e, "Cannot reach Xpattern server"); }
}

export async function relayRun(code, kind, target, commands = "", onProgress) {
  let job;
  try { job = await api.relayCreateJob(code, { kind, target, commands }); } catch (e) { throw apiError(e, "Could not queue job for the helper"); }
  const r = await relayWait(code, job.id, onProgress, kind);
  return { ...r, target: job.target, rerouted_from: job.rerouted_from || null };
}

export const STALL_MSG = "The plotter stopped taking data. It is paused, offline or was reset mid-cut: press its Online / Reset button, check the USB cable, then select the template and press Cut again.";
const PICKUP_MS = 45000; // helper must pick the job up within this
const STALL_MS = 40000; // streaming with no new bytes for this long = plotter not consuming
// Elevated repairs on the shop PC (UAC prompt, winmgmt salvage, pnputil) run for minutes with long silent stretches — the helper
// re-posts its progress every 3 s, so a fresh `progress.at` is the liveness signal and the limit matches the helper's own timeout.
const LONG_JOBS = { wmi_repair: 600000, driver_ch340: 300000 };
const LONG_MSG = "The shop PC stopped reporting on this repair. Make sure Xpattern Software is still running there and the Windows administrator prompt was answered, then try again.";

export async function relayWait(code, jobId, onProgress, kind = "cut") {
  const t0 = Date.now();
  const longMs = LONG_JOBS[kind];
  let lastSent = -1, lastMove = t0;
  for (;;) {
    let j;
    try { j = await api.relayJob(code, jobId, 20); } catch (e) { throw apiError(e, "Lost contact with Xpattern server"); }
    if (j.status === "done") return j.result || {};
    if (j.status === "failed") throw new Error(j.result?.error || "The helper reported an error");
    if (j.status === "canceled" || j.status === "expired") throw new Error(j.result?.error || (longMs ? "This repair was stopped" : "This cut was stopped"));
    const now = Date.now();
    if (j.status === "queued") {
      if (now - t0 > PICKUP_MS) throw new Error("Timed out waiting for the helper on the shop computer. Is the Xpattern Helper window still open?");
      continue;
    }
    const pr = j.progress;
    if (longMs) {
      const started = j.sent_at ? new Date(j.sent_at).getTime() : t0;
      const beat = pr?.at ? new Date(pr.at).getTime() : started;
      if (now - started > longMs || now - Math.max(beat, started) > 150000) throw new Error(LONG_MSG);
      continue;
    }
    if (pr && pr.sent !== lastSent) { lastSent = pr.sent; lastMove = now; onProgress?.(pr); }
    // a cut streamed straight over USB takes as long as the plotter needs — only give up when bytes stop moving
    const since = pr ? lastMove : (j.sent_at ? new Date(j.sent_at).getTime() : t0);
    if (now - since > (pr ? STALL_MS : 150000)) throw new Error(STALL_MSG);
  }
}

export const bridgeTarget = (c) =>
  c.targetType === "tcp" ? { type: "tcp", host: c.host, port: Number(c.port) || 9100 }
    : c.targetType === "printer" ? { type: "printer", printerName: c.printerName }
      : { type: "serial", serialPort: c.serialPort, baud: Number(c.baud) || 9600, flow: c.flow };

export const targetConfigured = (c) =>
  c.targetType === "printer" ? Boolean(c.printerName) : c.targetType === "serial" ? Boolean(c.serialPort) : Boolean(c.host) && c.host !== DEFAULT_CONNECTION.host;

export const applySuggested = (c, s) => ({ ...c, transport: "bridge", targetType: s.type, printerName: s.printerName || c.printerName, serialPort: s.serialPort || c.serialPort, baud: s.baud || c.baud, pinned: false });
